import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import {
  CalendarClock,
  Gavel,
  Loader2,
  Plus,
  RefreshCw,
  ShieldAlert,
  Settings2,
} from 'lucide-react'
import { useCapabilities } from '../hooks/useCapabilities'
import {
  useGetDisputeAccess,
  useGetDisputeBoard,
  useCreateDisputeMatter,
  usePushDisputeCalendar,
  useSaveDisputePolicy,
} from '../hooks/backend/disputes'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { Label } from '../lib/shadcn/label'
import { Switch } from '../lib/shadcn/switch'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../lib/shadcn/tabs'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { MatterDetail } from '../components/disputes/MatterDetail'
import { writeErrorMessage } from '../utils/writeError'
import {
  FORUMS,
  eventStateLabel,
  eventTone,
  formatDate,
  formatWhen,
  matterHeadline,
  matterNeeds,
  duplicateLine,
  nextEventSentence,
  type DisputeEvent,
  type DisputeMatter,
  type DuplicateMatter,
} from '../utils/disputes'

type Board = { matters: DisputeMatter[]; upcoming: DisputeEvent[]; duplicates?: DuplicateMatter[] }
type Access = {
  email: string
  can_manage: boolean
  is_admin: boolean
  policy: {
    owner_email: string
    notify_emails: string[]
    calendar_push_enabled: boolean
    calendar_id: string | null
    reminder_lead_days: number
    effective_from: string
    set_by: string | null
  } | null
}

/**
 * CCMA and Disputes.
 *
 * WHAT THIS SCREEN IS FOR, stated plainly because everything else follows from
 * it. A referral arrives in the post or by email. Somebody has to know it exists,
 * somebody has to be in a room on a particular morning, and if nobody is, section
 * 138(5)(b) of the Labour Relations Act lets the commissioner carry on without
 * Fleetly and issue an award. So the questions the page answers, in this order,
 * are: when is the next one, who is it for, and what is it about.
 *
 * THE ONE RULE THAT SHAPES EVERY PANEL. A model reads the document and proposes.
 * A person confirms. Only a confirmed date is a date: it is the only kind that
 * appears in the diary line at the top, the only kind that sets a task's target
 * date, and the only kind that reaches Google Calendar. An unconfirmed date is
 * rendered amber with the words "read from the document, not yet confirmed" and
 * is never merged into the confirmed count. That is deliberately harder than the
 * platform request board's version of the same rule, because a misread severity
 * wastes an afternoon and a misread hearing date loses a case.
 *
 * WHAT IT DELIBERATELY WILL NOT DO. It computes no statutory time limit: not the
 * thirty days, not the fourteen days, not the six weeks. Those are qualified by
 * condonation and by forum, and Fleetly takes them from its labour advisor. A
 * recommendation carries a date only where the document itself prints one, and a
 * person types every other date themselves.
 */
export default function Disputes() {
  const { loading: accessLoading, canView, caption } = useCapabilities()
  const board = useGetDisputeBoard<Board>()
  const access = useGetDisputeAccess<Access>()
  const create = useCreateDisputeMatter()
  const push = usePushDisputeCalendar()

  const boardTrigger = board.trigger
  const accessTrigger = access.trigger

  const [openId, setOpenId] = useState<string | null>(null)
  const [showClosed, setShowClosed] = useState(false)
  const [forum, setForum] = useState<string>('CCMA')
  const [busy, setBusy] = useState<string | null>(null)

  const load = useCallback(() => {
    boardTrigger({}, { skipCache: true })
  }, [boardTrigger])

  useEffect(() => {
    if (canView) {
      load()
      accessTrigger({}, { skipCache: true })
    }
  }, [canView, load, accessTrigger])

  const matters = board.data?.matters ?? []
  const upcoming = board.data?.upcoming ?? []
  // Optional because a Worker deployed before migration 2830 does not send it.
  const duplicates = board.data?.duplicates ?? []
  const canManage = access.data?.can_manage === true
  const policy = access.data?.policy ?? null

  const visible = useMemo(
    () => matters.filter((m) => (showClosed ? true : m.is_open)),
    [matters, showClosed],
  )

  // The diary line reads from every live event, not only the confirmed ones, so
  // that a matter whose only date is unconfirmed produces "no confirmed date"
  // rather than silence. Silence would read as "nothing coming up".
  const allLiveEvents = useMemo(() => {
    const seen = new Map<string, DisputeEvent>()
    for (const e of upcoming) seen.set(e.id, e)
    return Array.from(seen.values())
  }, [upcoming])

  const next = useMemo(() => nextEventSentence(allLiveEvents), [allLiveEvents])

  const openCount = matters.filter((m) => m.is_open).length
  const needsSomething = matters.filter((m) => matterNeeds(m) !== null).length
  const unconfirmed = matters.reduce((n, m) => n + (m.is_open ? m.events_unconfirmed : 0), 0)

  const onNew = useCallback(async () => {
    setBusy('new')
    try {
      const res = (await create.trigger({ forum }, { skipCache: true })) as { id?: string } | null
      if (res?.id) {
        setOpenId(res.id)
        toast.success('Matter opened and a task raised. Upload the document next.')
      }
      load()
    } catch (err) {
      toast.error(writeErrorMessage(err))
    } finally {
      setBusy(null)
    }
  }, [create, forum, load])

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  if (!canView) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            CCMA and Disputes is available to signed-in Fleetly staff. Ask an admin to add you to
            cockpit access.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 bg-card">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="border-b border-border px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
              <Gavel className="h-5 w-5" />
            </div>
            <h1 className="shrink-0 whitespace-nowrap text-lg font-semibold text-foreground">
              CCMA and Disputes
            </h1>
            <div className="ml-auto flex items-center gap-2">
              <NativeSelect
                className="h-8 w-48 text-xs"
                value={forum}
                onChange={(e) => setForum(e.target.value)}
                aria-label="Which forum"
              >
                {FORUMS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </NativeSelect>
              <Button size="sm" variant="brand" disabled={busy !== null} onClick={() => void onNew()}>
                {busy === 'new' ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                )}
                A document arrived
              </Button>
              <Button size="sm" variant="ghost" onClick={load} aria-label="Refresh">
                <RefreshCw className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{caption}</p>

          {/* The one line the page exists for. */}
          <div
            className={cn(
              'mt-3 rounded-md border p-3',
              next.tone === 'red'
                ? 'border-rose-300 bg-rose-50 dark:border-rose-800 dark:bg-rose-950'
                : next.tone === 'amber'
                  ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950'
                  : next.tone === 'green'
                    ? 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950'
                    : 'border-border bg-background',
            )}
          >
            <div className="flex items-center gap-2">
              <CalendarClock className="h-4 w-4 shrink-0 text-foreground" />
              <p className="text-sm font-semibold text-foreground">{next.headline}</p>
            </div>
            {next.detail && <p className="mt-1 text-xs text-foreground">{next.detail}</p>}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            <Chip tone={openCount > 0 ? 'brand' : 'neutral'}>
              Open<span className="ml-1 font-semibold tabular-nums">{openCount}</span>
            </Chip>
            <Chip tone={needsSomething > 0 ? 'amber' : 'green'}>
              Waiting on somebody
              <span className="ml-1 font-semibold tabular-nums">{needsSomething}</span>
            </Chip>
            {/* Counted separately and never folded into the confirmed diary. */}
            <Chip tone={unconfirmed > 0 ? 'amber' : 'neutral'}>
              Dates not yet confirmed
              <span className="ml-1 font-semibold tabular-nums">{unconfirmed}</span>
            </Chip>
            <label className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
              <Switch checked={showClosed} onCheckedChange={setShowClosed} />
              Show closed
            </label>
          </div>
        </div>

        <Tabs defaultValue="matters" className="flex min-h-0 flex-1 flex-col">
          <div className="border-b border-border px-4 py-2">
            <TabsList>
              <TabsTrigger value="matters">Matters</TabsTrigger>
              <TabsTrigger value="calendar">Calendar</TabsTrigger>
              {access.data?.is_admin && <TabsTrigger value="settings">Settings</TabsTrigger>}
            </TabsList>
          </div>

          <TabsContent value="matters" className="min-h-0 flex-1 overflow-y-auto p-4">
            {board.loading && matters.length === 0 ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner className="h-4 w-4" /> Loading…
              </div>
            ) : visible.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No matter on file. That is the good state. When a letter arrives, press "A document
                arrived", upload it, and the rest follows from the paper.
              </p>
            ) : (
              <ul className="space-y-2">
                {visible.map((m) => {
                  const needs = matterNeeds(m)
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        onClick={() => setOpenId(m.id)}
                        className={cn(
                          'w-full rounded-md border p-3 text-left transition-colors hover:bg-accent',
                          openId === m.id ? 'border-[hsl(var(--brand))]' : 'border-border',
                        )}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-foreground">
                            {matterHeadline(m)}
                          </span>
                          <Chip tone="neutral">{m.forum}</Chip>
                          <Chip tone={m.is_open ? 'brand' : 'neutral'}>{m.status}</Chip>
                          {m.driver_link === 'no driver record' && (
                            <Chip tone="neutral">not on our books</Chip>
                          )}
                          {needs && <Chip tone="amber">{needs}</Chip>}
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {m.matter_type ?? 'kind of matter not yet confirmed'}
                          {'. '}
                          {m.next_confirmed_on
                            ? `Next confirmed date ${formatDate(m.next_confirmed_on)}.`
                            : 'No confirmed date.'}
                          {m.driver_manager ? ` Manager ${m.driver_manager}.` : ''}
                        </p>
                      </button>
                    </li>
                  )
                })}
              </ul>
            )}
            {/* Withdrawn duplicates are off the board and out of every count, but
                not out of sight: a filing that vanished without a word is how it
                gets filed a third time. Platform request #7. */}
            {duplicates.length > 0 && (
              <div className="mt-6 border-t border-border pt-3">
                <p className="text-xs font-medium text-muted-foreground">
                  Withdrawn as filed twice ({duplicates.length}). Kept on record, not deleted.
                </p>
                <ul className="mt-1 space-y-1">
                  {duplicates.map((d) => (
                    <li key={d.id}>
                      <button
                        type="button"
                        onClick={() => setOpenId(d.id)}
                        className="text-left text-xs text-muted-foreground underline-offset-2 hover:underline"
                      >
                        {duplicateLine(d)}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </TabsContent>

          <TabsContent value="calendar" className="min-h-0 flex-1 overflow-y-auto p-4">
            <div className="mb-3 flex items-center gap-2">
              <p className="text-xs text-muted-foreground">
                Confirmed and unconfirmed together, never merged. Amber has not been checked against
                the document by a person and is in nobody's diary.
              </p>
              {policy?.calendar_push_enabled && (
                <Button
                  size="sm"
                  variant="outline"
                  className="ml-auto"
                  disabled={busy !== null}
                  onClick={async () => {
                    setBusy('push')
                    try {
                      const r = (await push.trigger({}, { skipCache: true })) as {
                        pushed?: number
                        failed?: number
                        note?: string | null
                      } | null
                      toast.success(
                        r?.note ?? `${r?.pushed ?? 0} written, ${r?.failed ?? 0} refused.`,
                      )
                      load()
                    } catch (err) {
                      toast.error(writeErrorMessage(err))
                    } finally {
                      setBusy(null)
                    }
                  }}
                >
                  {busy === 'push' ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <CalendarClock className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Write confirmed dates to the calendar
                </Button>
              )}
            </div>
            {upcoming.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing confirmed and coming up.</p>
            ) : (
              <ul className="space-y-2">
                {upcoming.map((e) => (
                  <li key={e.id} className="rounded-md border border-border p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">{e.event_type}</span>
                      <span className="text-sm text-foreground">{formatWhen(e)}</span>
                      <Chip tone={eventTone(e)}>{eventStateLabel(e)}</Chip>
                      <button
                        type="button"
                        className="ml-auto text-xs underline-offset-2 hover:underline"
                        onClick={() => setOpenId(e.matter_id)}
                      >
                        Open matter #{e.matter_ref}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {e.driver_name ?? e.applicant_name ?? 'applicant not yet named'}
                      {e.case_number ? `, case ${e.case_number}` : ', case number not on file'}
                      {e.venue ? `. ${e.venue}` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>

          {access.data?.is_admin && (
            <TabsContent value="settings" className="min-h-0 flex-1 overflow-y-auto p-4">
              <PolicyPanel
                policy={policy}
                onSaved={() => {
                  accessTrigger({}, { skipCache: true })
                  load()
                }}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>

      {openId && (
        <aside className="hidden w-[520px] shrink-0 border-l border-border lg:block">
          <MatterDetail
            matterId={openId}
            canManage={canManage}
            onChanged={load}
            onClose={() => setOpenId(null)}
          />
        </aside>
      )}
    </div>
  )
}

function PolicyPanel({
  policy,
  onSaved,
}: {
  policy: Access['policy']
  onSaved: () => void
}) {
  const save = useSaveDisputePolicy()
  const [owner, setOwner] = useState(policy?.owner_email ?? '')
  const [notify, setNotify] = useState((policy?.notify_emails ?? []).join(', '))
  const [calOn, setCalOn] = useState(policy?.calendar_push_enabled ?? false)
  const [calId, setCalId] = useState(policy?.calendar_id ?? '')
  const [lead, setLead] = useState(String(policy?.reminder_lead_days ?? 14))
  const [saving, setSaving] = useState(false)

  return (
    <div className="max-w-2xl space-y-3">
      <div className="flex items-center gap-2">
        <Settings2 className="h-4 w-4" />
        <h2 className="text-sm font-semibold text-foreground">Who carries disputes</h2>
      </div>
      <p className="text-xs text-muted-foreground">
        Saving closes the current row and opens a new one, so who carried CCMA matters and when the
        calendar was switched on stays readable afterwards. Nothing here is a code change.
        {policy?.effective_from
          ? ` In force since ${formatDate(policy.effective_from)}${policy.set_by ? `, set by ${policy.set_by}` : ''}.`
          : ' No policy row is in force, which is why matters cannot be filed yet.'}
      </p>

      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <Label className="text-xs">Owner (a cockpit login)</Label>
          <Input className="h-8 text-sm" value={owner} onChange={(e) => setOwner(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Also notify, comma separated</Label>
          <Input className="h-8 text-sm" value={notify} onChange={(e) => setNotify(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Reminder task opens this many days before</Label>
          <Input
            className="h-8 text-sm"
            type="number"
            value={lead}
            onChange={(e) => setLead(e.target.value)}
          />
        </div>
        <div className="flex items-end gap-2">
          <Switch checked={calOn} onCheckedChange={setCalOn} />
          <span className="text-xs text-muted-foreground">Write confirmed dates to Google Calendar</span>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs">Google Calendar id</Label>
          <Input className="h-8 text-sm" value={calId} onChange={(e) => setCalId(e.target.value)} />
          <p className="mt-1 text-[11px] text-muted-foreground">
            Create a calendar in your own Google account and paste its id here. It is not shared
            with the service account: Fleetly writes to it as the person named in
            GOOGLE_IMPERSONATE_SUBJECT, who has to own it. Until that is done the push stays off
            and nothing is written anywhere. Only confirmed dates are ever eligible.
          </p>
        </div>
      </div>

      <Button
        size="sm"
        variant="brand"
        disabled={saving || !owner.trim()}
        onClick={async () => {
          setSaving(true)
          try {
            await save.trigger(
              {
                owner_email: owner.trim(),
                notify_emails: notify
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean),
                calendar_push_enabled: calOn,
                calendar_id: calId.trim() || null,
                reminder_lead_days: Number(lead) || 14,
              },
              { skipCache: true },
            )
            toast.success('Saved as a new policy row.')
            onSaved()
          } catch (err) {
            toast.error(writeErrorMessage(err))
          } finally {
            setSaving(false)
          }
        }}
      >
        {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
        Save
      </Button>
    </div>
  )
}
